import {
  getAppName,
  getKnownAppIcon,
} from "@/components/shapes/connection-frame/app-icons";
import type {
  WorkToastAppRef,
  WorkToastItem,
  WorkToastOpenTarget,
} from "@/stores/work-toast-store";
import { translate } from "@/i18n";
import { libNoticesMessages } from "@/i18n/messages/lib-notices";

const APP_ROUTES = new Set(["ax", "mcp", "provider"]);

type Payload = Record<string, unknown>;

/**
 * Merge dispatch intent (confirmed=false) with tool receipts (confirmed=true);
 * receipts win per appId. An app goes to the end of the list when a receipt
 * first confirms it, so the end is the app whose first touch is the latest.
 * The backend reports each receipt once, in the brief's order, so a later
 * touch of an app that is already confirmed is not observable here and that
 * app keeps its place. The newest eight are kept.
 */
export function mergeWorkToastApps(
  current: WorkToastAppRef[],
  incoming: WorkToastAppRef[],
): WorkToastAppRef[] {
  const merged = new Map<string, WorkToastAppRef>();
  for (const ref of current) merged.set(ref.appId.toLowerCase(), ref);
  for (const ref of incoming) {
    const key = ref.appId.toLowerCase();
    const prev = merged.get(key);
    if (prev?.confirmed && !ref.confirmed) continue;
    // Re-inserting moves the key to the end; a plain set keeps its place.
    if (prev && !prev.confirmed && ref.confirmed) merged.delete(key);
    merged.set(key, ref);
  }
  return [...merged.values()].slice(-8);
}

/** Parse an apps payload (`apps` or `app_refs`, camelCase or snake_case). Null when absent. */
export function parseWorkToastApps(data: Payload): WorkToastAppRef[] | null {
  const raw = data.apps ?? data.app_refs;
  if (!Array.isArray(raw)) return null;
  const refs: WorkToastAppRef[] = [];
  for (const entry of raw) {
    const ref = entry as Payload | null;
    const appId =
      typeof ref?.appId === "string"
        ? ref.appId
        : typeof ref?.app_id === "string"
          ? ref.app_id
          : null;
    if (!appId?.trim()) continue;
    const route =
      typeof ref?.route === "string" && APP_ROUTES.has(ref.route)
        ? (ref.route as WorkToastAppRef["route"])
        : "provider";
    const label =
      typeof ref?.label === "string" && ref.label.trim()
        ? ref.label.trim()
        : undefined;
    const iconUrl = typeof ref?.iconUrl === "string" ? ref.iconUrl : undefined;
    refs.push({
      appId: appId.trim().toLowerCase(),
      route,
      confirmed: ref?.confirmed === true,
      ...(label ? { label } : {}),
      ...(iconUrl ? { iconUrl } : {}),
      briefOrder: refs.length,
    });
  }
  return refs;
}

/** Parse app destinations (`open_targets` or `openTargets`). Null when absent.
 *  Newer payloads call the destination `uri`; `url` remains populated so old
 *  renderers and mixed-version mirrored windows continue to work. `kind:
 *  "app"` entries (url-less desktop-app targets) are valid without a
 *  uri/url; everything else still requires one. */
export function parseWorkToastOpenTargets(
  data: Payload,
): WorkToastOpenTarget[] | null {
  const raw = data.open_targets ?? data.openTargets;
  if (!Array.isArray(raw)) return null;
  const targets: WorkToastOpenTarget[] = [];
  for (const entry of raw) {
    const target = entry as Payload | null;
    const appId =
      typeof target?.appId === "string"
        ? target.appId
        : typeof target?.app_id === "string"
          ? target.app_id
          : null;
    if (!appId?.trim()) continue;
    const kind =
      target?.kind === "app" || target?.kind === "resource"
        ? target.kind
        : undefined;
    const rawUri =
      typeof target?.uri === "string"
        ? target.uri
        : typeof target?.url === "string"
          ? target.url
          : null;
    const normalizedUri = rawUri?.trim() ? rawUri.trim() : null;
    if (kind !== "app" && !normalizedUri) continue;
    const route =
      typeof target?.route === "string" && APP_ROUTES.has(target.route)
        ? (target.route as WorkToastAppRef["route"])
        : undefined;
    const expectedBundleId =
      typeof target?.expectedBundleId === "string"
        ? target.expectedBundleId
        : typeof target?.expected_bundle_id === "string"
          ? target.expected_bundle_id
          : undefined;
    targets.push({
      appId: appId.trim().toLowerCase(),
      label:
        typeof target?.label === "string" && target.label.trim()
          ? target.label
          : appId.trim(),
      ...(typeof target?.id === "string" && target.id.trim()
        ? { id: target.id.trim() }
        : {}),
      ...(kind ? { kind } : {}),
      ...(route ? { route } : {}),
      ...(normalizedUri ? { url: normalizedUri, uri: normalizedUri } : {}),
      ...(expectedBundleId?.trim()
        ? { expectedBundleId: expectedBundleId.trim() }
        : {}),
    });
  }
  return targets;
}

/** Apps attached to a toast item (never null at render time). */
export function workToastApps(
  item: Pick<WorkToastItem, "apps">,
): WorkToastAppRef[] {
  return item.apps ?? [];
}

/**
 * Display apps for the pill logo: explicit `apps` first, falling back to
 * `openTargets` so a task that revolves around an app (e.g. Slack) still
 * shows that app's icon instead of Denker's avatar when the backend sent
 * no app refs (older payloads, or the tool call failed before a receipt).
 */
export function workToastDisplayApps(
  item: Pick<WorkToastItem, "apps"> & {
    openTargets?: WorkToastItem["openTargets"];
    computer?: WorkToastItem["computer"];
  },
): WorkToastAppRef[] {
  const apps = workToastApps(item);
  if (apps.length > 0) return apps;
  const seen = new Set<string>();
  const fallback: WorkToastAppRef[] = [];
  for (const target of workToastOpenTargets(item)) {
    const key = target.appId.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    fallback.push({
      appId: target.appId,
      route: target.route ?? "mcp",
      confirmed: true,
    });
  }
  if (
    fallback.length === 0 &&
    item.computer?.appLabel?.trim() &&
    getKnownAppIcon(item.computer.appLabel)
  ) {
    fallback.push({
      appId: item.computer.appLabel.trim().toLowerCase(),
      route: "ax",
      confirmed: true,
      label: item.computer.appLabel.trim(),
    });
  }
  return fallback;
}

/** An app's display name, without waiting for its icon to resolve. */
function workToastAppName(ref: WorkToastAppRef): string {
  return ref.label?.trim() || getAppName(ref.appId);
}

/** The ref a name refers to: its label, its catalogue name or its id. */
export function workToastAppNamed(
  apps: WorkToastAppRef[],
  name: string,
): WorkToastAppRef | undefined {
  const wanted = name.trim().toLowerCase();
  if (!wanted) return undefined;
  return apps.find(
    (ref) =>
      workToastAppName(ref).toLowerCase() === wanted ||
      getAppName(ref.appId).toLowerCase() === wanted ||
      ref.appId.toLowerCase() === wanted,
  );
}

/**
 * The apps a toast stands for, most recent first. The logo shows the first;
 * the rest are counted on its badge.
 *
 * Among the apps the work has touched (`confirmed`), the last in the list is
 * the most recent, as far as each writer can tell: the computer-use feed moves
 * a desktop or connector app to the end on every touch; `mergeWorkToastApps`
 * moves an app to the end when a backend receipt first confirms it, and
 * cannot see a later touch of it; adoption appends the standalone card's apps
 * after the task's. Apps that were only declared follow, in the brief's
 * order. The app the current step names leads, so the icon agrees with the
 * step line. One application listed under two ids counts once, as its most
 * recent entry. Open targets, which stand in when there are no app refs, are
 * destinations and not touches: they keep their own order, primary first.
 */
export function workToastRecentApps(
  item: Parameters<typeof workToastDisplayApps>[0] &
    Partial<Pick<WorkToastItem, "state" | "activity">>,
): WorkToastAppRef[] {
  const apps = workToastDisplayApps(item);
  if (workToastApps(item).length === 0) return apps;
  const recent = [
    ...apps.filter((ref) => ref.confirmed).reverse(),
    ...apps.filter((ref) => !ref.confirmed),
  ];
  const stepApp =
    item.state === "working"
      ? item.activity?.[item.activity.length - 1]?.app
      : undefined;
  const current = stepApp ? workToastAppNamed(recent, stepApp) : undefined;
  const ordered = current
    ? [current, ...recent.filter((ref) => ref !== current)]
    : recent;
  const names = new Set<string>();
  return ordered.filter((ref) => {
    const name = workToastAppName(ref).toLowerCase();
    return !names.has(name) && Boolean(names.add(name));
  });
}

/** "Slack and 2 more apps: Gmail, Notion": every app by name, in the given order. */
export function workToastAppsLabel(apps: WorkToastAppRef[]): string {
  const [first, ...rest] = apps.map(workToastAppName);
  if (!first) return "";
  if (rest.length === 0) return first;
  return translate(libNoticesMessages, "appsMore", {
    first,
    count: rest.length,
    rest: rest.join(", "),
  });
}

/** Verify-in-app targets attached to a toast item. */
export function workToastOpenTargets(item: {
  openTargets?: WorkToastItem["openTargets"];
}): WorkToastOpenTarget[] {
  return item.openTargets ?? [];
}

/**
 * The single primary-target notion for a toast: the target for the primary
 * app, else the first open target. Null when there are no targets at all.
 * The primary app is the first in the brief's order (`briefOrder`), which the
 * list no longer shows once apps are ordered by touch; a ref without a place
 * in the brief follows, in list order.
 */
export function workToastPrimaryOpenTarget(
  item: Pick<WorkToastItem, "apps" | "openTargets">,
): WorkToastOpenTarget | null {
  const targets = item.openTargets ?? [];
  if (targets.length === 0) return null;
  const byBrief = [...(item.apps ?? [])].sort(
    (a, b) =>
      (a.briefOrder ?? Number.MAX_SAFE_INTEGER) -
      (b.briefOrder ?? Number.MAX_SAFE_INTEGER),
  );
  for (const app of byBrief) {
    const match = targets.find((t) => t.appId === app.appId);
    if (match) return match;
  }
  return targets[0] ?? null;
}

/** Primary target's display label, e.g. "Open in Notion". Null when no targets. */
export function workToastOpenInAppLabel(
  item: Pick<WorkToastItem, "apps" | "openTargets">,
): string | null {
  const primary = workToastPrimaryOpenTarget(item);
  if (!primary) return null;
  return translate(libNoticesMessages, "openInApp", {
    app: getAppName(primary.appId),
  });
}

/** Whether the toast's work used the computer: a step or a screen capture. */
export function workToastUsedComputer(
  item: Partial<Pick<WorkToastItem, "computer" | "computerScreenshots">>,
): boolean {
  return Boolean(item.computer) || Boolean(item.computerScreenshots?.length);
}
